import axios from 'axios';
import type {
  SystemStatus,
  SitesStatus,
  SeedRequest,
  SeedResponse,
  User,
  UserCreate,
  UserUpdate,
  DeactivateUserRequest,
  CancelSubscriptionRequest,
  SiteDetail,
  SiteCreate,
  SiteUpdate,
  NEMPricesResponse,
  NEMRegionData,
} from '../types';

// API base URL - always use /api which nginx proxies to the backend
// This works both in development (Vite proxy) and production (nginx proxy)
const API_BASE = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Storage keys - must match AuthContext
const TOKEN_KEY = 'solaniq_token';
const USER_KEY = 'solaniq_user';

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors (401). The React AuthProvider registers a handler via
// setUnauthorizedHandler() so the app can clear session state and let React
// Router redirect smoothly. We deliberately do NOT do `window.location.href =
// '/login'` here: that hard page reload caused a visible flash of whatever was
// rendering (e.g. the register form, or a logged-in dashboard with a stale
// token) before the browser navigated away.
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Drop cached credentials so a stale token isn't reused.
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      delete api.defaults.headers.common['Authorization'];
      // Let the app update auth state; ProtectedRoute then redirects via the
      // SPA router (no full reload).
      onUnauthorized?.();
    }
    return Promise.reject(error);
  }
);

// System status
export async function getSystemStatus(): Promise<SystemStatus> {
  const response = await api.get<SystemStatus>('/status');
  return response.data;
}

// Sites status
export async function getSitesStatus(): Promise<SitesStatus> {
  const response = await api.get<SitesStatus>('/status/sites');
  return response.data;
}

// Health check
export async function healthCheck(): Promise<{ status: string }> {
  const response = await api.get<{ status: string }>('/health');
  return response.data;
}

// ============== Registration API ==============

export interface SiteRegistrationData {
  name: string;
  region: string;
  latitude?: number;
  longitude?: number;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
  battery_adapter: string;
  battery_credentials?: Record<string, unknown>;
  battery_config?: Record<string, unknown>;
  solar_config?: Record<string, unknown>;
  price_provider?: string;
  price_provider_credentials?: Record<string, unknown>;
  forecast_provider?: string;
  forecast_provider_credentials?: Record<string, unknown>;
  optimizer_engine?: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  promo_codes?: string[];
  site: SiteRegistrationData;
}

export interface SubscriptionInfo {
  status: string;
  monthly_price_cents: number;
  monthly_price_formatted: string;
  promo_codes: string[] | null;
  promo_descriptions: string[] | null;
  trial_end: string | null;
  created_at: string;
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  user: {
    id: string;
    email: string;
    role: string;
    is_active: boolean;
    is_verified: boolean;
  };
  site_id: string;
  subscription: SubscriptionInfo;
  stripe_checkout_url?: string;  // URL to complete payment if Stripe is enabled
}

/**
 * Register a new user with their first site.
 * Supports multiple promo codes that can be stacked:
 * - FREETRIAL (30 days free)
 * - FOUNDINGMEMBER (50% off)
 */
export async function registerUser(data: RegisterRequest): Promise<RegisterResponse> {
  const response = await api.post<RegisterResponse>('/auth/register', data);
  return response.data;
}

export interface CompleteRegistrationRequest {
  promo_codes?: string[];
  /** Existing site to update (defaults to the user's first site, else a new one). */
  site_id?: string;
  site?: SiteRegistrationData;
}

export interface CompleteRegistrationResponse {
  success: boolean;
  message: string;
  user: User;
  site_id: string | null;
  subscription: SubscriptionInfo;
  stripe_checkout_url?: string | null;
}

/**
 * Finish sign-up for a logged-in account with no live subscription (social
 * sign-ins, lapsed subscriptions). Saves the site details and returns a
 * Stripe Checkout URL when billing is enabled.
 */
export async function completeRegistration(
  data: CompleteRegistrationRequest
): Promise<CompleteRegistrationResponse> {
  const response = await api.post<CompleteRegistrationResponse>(
    '/auth/me/complete-registration',
    data
  );
  return response.data;
}

// ============== OAuth Social Login ==============

/** Response shape shared by /auth/login and the /auth/oauth/* endpoints. */
export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: {
    id: string;
    email: string;
    role: 'admin' | 'user';
    is_active: boolean;
    is_verified: boolean;
  };
}

/**
 * Exchange a Google Identity Services ID token for a SolanIQ session.
 * 401 = token rejected, 403 = unverified provider email, 501 = provider not configured.
 */
export async function loginWithGoogle(idToken: string): Promise<TokenResponse> {
  const response = await api.post<TokenResponse>('/auth/oauth/google', { id_token: idToken });
  return response.data;
}

/**
 * Exchange a Sign in with Apple ID token for a SolanIQ session.
 * `fullName` is only available on the user's first authorization — pass it
 * through so the backend can capture the name Apple never sends again.
 */
export async function loginWithApple(idToken: string, fullName?: string): Promise<TokenResponse> {
  const payload: { id_token: string; full_name?: string } = { id_token: idToken };
  if (fullName) payload.full_name = fullName;
  const response = await api.post<TokenResponse>('/auth/oauth/apple', payload);
  return response.data;
}

// ============== Account member invites ==============

export interface InvitePreview {
  valid: boolean;
  owner_email_masked: string | null;
  expires_at: string | null;
}

/** Public: is an "Accept invite" link still good, and whose home is it (masked)? */
export async function previewInvite(token: string): Promise<InvitePreview> {
  const response = await api.get<InvitePreview>(`/invites/${encodeURIComponent(token)}`);
  return response.data;
}

/** Accept an invite by link token or code, from any signed-in account. */
export async function acceptInvite(
  body: { token?: string; code?: string }
): Promise<{ id: string; owner_email: string; status: string }> {
  const response = await api.post('/me/invites/accept', body);
  return response.data;
}

// Seed demo data
export async function seedDemoData(request: SeedRequest = {}): Promise<SeedResponse> {
  const response = await api.post<SeedResponse>('/admin/seed', request);
  return response.data;
}

// Clear demo data
export async function clearDemoData(): Promise<{ success: boolean; deleted: Record<string, number> }> {
  const response = await api.delete('/admin/seed');
  return response.data;
}

// ============== Users API ==============

export async function getUsers(): Promise<User[]> {
  const response = await api.get<User[]>('/users');
  return response.data;
}

export async function getUser(userId: string): Promise<User> {
  const response = await api.get<User>(`/users/${userId}`);
  return response.data;
}

export async function createUser(data: UserCreate): Promise<User> {
  const response = await api.post<User>('/users', data);
  return response.data;
}

export async function updateUser(userId: string, data: UserUpdate): Promise<User> {
  const response = await api.put<User>(`/users/${userId}`, data);
  return response.data;
}

export async function deleteUser(userId: string): Promise<void> {
  await api.delete(`/users/${userId}`);
}

export async function deactivateUser(
  userId: string,
  options: DeactivateUserRequest = {}
): Promise<User> {
  const response = await api.post<User>(`/users/${userId}/deactivate`, options);
  return response.data;
}

export async function reactivateUser(userId: string): Promise<User> {
  const response = await api.post<User>(`/users/${userId}/reactivate`);
  return response.data;
}

export async function cancelUserSubscription(
  userId: string,
  options: CancelSubscriptionRequest = {}
): Promise<User> {
  const response = await api.post<User>(`/users/${userId}/cancel-subscription`, options);
  return response.data;
}

/** Admin: pull one user's billing state (customer, subscription, status) from Stripe. */
export async function syncUserSubscription(userId: string): Promise<User> {
  const response = await api.post<User>(`/users/${userId}/sync-subscription`);
  return response.data;
}

export interface SubscriptionSyncResult {
  user_id: string;
  email: string;
  changed: boolean;
  matched_by: 'customer_id' | 'email' | null;
  subscription_count: number;
  before: Record<string, string | null>;
  after: Record<string, string | null>;
  error?: string | null;
}

export interface SubscriptionSyncSummary {
  total: number;
  changed: number;
  unmatched: number;
  errors: number;
  sites_suspended?: number;
  sites_restored?: number;
  results: SubscriptionSyncResult[];
}

/** Admin: reconcile every user's billing state with Stripe. */
export async function syncAllSubscriptions(): Promise<SubscriptionSyncSummary> {
  const response = await api.post<SubscriptionSyncSummary>('/users/sync-subscriptions');
  return response.data;
}

export interface EntitlementResult {
  user_id: string;
  email: string;
  subscription_status: string | null;
  entitled: boolean;
  sites_suspended: number;
  sites_restored: number;
}

export interface EntitlementSummary {
  total: number;
  unentitled: number;
  sites_suspended: number;
  sites_restored: number;
  results: EntitlementResult[];
}

/** Admin: switch off sites of unsubscribed users (and back on once they subscribe). */
export async function enforceEntitlements(): Promise<EntitlementSummary> {
  const response = await api.post<EntitlementSummary>('/users/enforce-entitlements');
  return response.data;
}

// ============== Sites API ==============

export async function getSites(filters?: { user_id?: string; region?: string; active?: boolean }): Promise<SiteDetail[]> {
  const params = new URLSearchParams();
  if (filters?.user_id) params.append('user_id', filters.user_id);
  if (filters?.region) params.append('region', filters.region);
  if (filters?.active !== undefined) params.append('active', String(filters.active));

  const response = await api.get<SiteDetail[]>(`/sites?${params.toString()}`);
  return response.data;
}

export async function getSite(siteId: string): Promise<SiteDetail> {
  const response = await api.get<SiteDetail>(`/sites/${siteId}`);
  return response.data;
}

export async function createSite(data: SiteCreate): Promise<SiteDetail> {
  const response = await api.post<SiteDetail>('/sites', data);
  return response.data;
}

export async function updateSite(siteId: string, data: SiteUpdate): Promise<SiteDetail> {
  const response = await api.put<SiteDetail>(`/sites/${siteId}`, data);
  return response.data;
}

export async function deleteSite(siteId: string): Promise<void> {
  await api.delete(`/sites/${siteId}`);
}

export interface SiteCredentials {
  site_id: string;
  price_provider_credentials: Record<string, unknown> | null;
  forecast_provider_credentials: Record<string, unknown> | null;
  battery_credentials: Record<string, unknown> | null;
  solar_credentials: Record<string, unknown> | null;
}

/**
 * Fetch a site's raw credentials. Owner or admin only.
 * Used by the admin Edit Site dialog to reveal currently-stored API keys
 * without populating them into editable input fields.
 */
export async function getSiteCredentials(siteId: string): Promise<SiteCredentials> {
  const response = await api.get<SiteCredentials>(`/sites/${siteId}/credentials`);
  return response.data;
}

// ============== Self-service Account API (/auth/me/*) ==============

export interface MeProfileUpdate {
  email?: string;
}

/** Re-fetch the logged-in user's profile from /auth/me. Used to refresh
 * cached fields (is_verified, subscription_status, …) that may have
 * been changed server-side since login. */
export async function getMe(): Promise<User> {
  const response = await api.get<User>('/auth/me');
  return response.data;
}

export async function updateMyProfile(data: MeProfileUpdate): Promise<User> {
  const response = await api.patch<User>('/auth/me/profile', data);
  return response.data;
}

export interface NotificationKind {
  kind: string;
  label: string;
}

export interface NotificationPreferences {
  preferences: Record<string, boolean>;
  kinds: NotificationKind[];
}

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  const response = await api.get<NotificationPreferences>(
    '/auth/me/notification-preferences',
  );
  return response.data;
}

export async function updateNotificationPreferences(
  preferences: Record<string, boolean>,
): Promise<NotificationPreferences> {
  const response = await api.put<NotificationPreferences>(
    '/auth/me/notification-preferences',
    { preferences },
  );
  return response.data;
}

export interface MySubscriptionPrice {
  id: string;
  currency: string;
  unit_amount: number | null;
  interval: string | null;
  product_name: string | null;
}

export interface MySubscription {
  has_subscription: boolean;
  id?: string;
  status?: string | null;
  cancel_at_period_end?: boolean;
  current_period_start?: number | null;
  current_period_end?: number | null;
  trial_end?: number | null;
  canceled_at?: number | null;
  price?: MySubscriptionPrice | null;
}

export async function getMySubscription(): Promise<MySubscription> {
  const response = await api.get<MySubscription>('/auth/me/subscription');
  return response.data;
}

export interface MyInvoice {
  id: string;
  number: string | null;
  status: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  created: number;
  period_start: number;
  period_end: number;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

export async function getMyInvoices(limit: number = 12): Promise<MyInvoice[]> {
  const response = await api.get<MyInvoice[]>(`/auth/me/invoices?limit=${limit}`);
  return response.data;
}

export async function createMyBillingPortalSession(): Promise<{ url: string }> {
  const response = await api.post<{ url: string }>('/auth/me/billing-portal', {});
  return response.data;
}

export async function cancelMySubscription(
  options: { immediately?: boolean } = {}
): Promise<{ subscription_id: string; status: string; cancel_at_period_end: boolean }> {
  const response = await api.post('/auth/me/cancel-subscription', options);
  return response.data;
}

export async function deleteMyAccount(confirm_email: string): Promise<{ ok: boolean }> {
  const response = await api.delete('/auth/me/account', { data: { confirm_email } });
  return response.data;
}

export async function sendVerificationEmail(): Promise<{ ok: boolean; already_verified?: boolean }> {
  const response = await api.post('/auth/me/send-verification-email', {});
  return response.data;
}

export async function verifyEmailWithToken(token: string): Promise<{ ok: boolean }> {
  const response = await api.post('/auth/verify-email', { token });
  return response.data;
}

export async function forgotPassword(email: string): Promise<{ ok: boolean }> {
  const response = await api.post('/auth/forgot-password', { email });
  return response.data;
}

export async function resetPassword(token: string, password: string): Promise<{ ok: boolean }> {
  const response = await api.post('/auth/reset-password', { token, password });
  return response.data;
}

// ============== Audit log API ==============

export interface AuditEvent {
  id: string;
  created_at: string;
  actor_user_id: string | null;
  actor_email: string | null;
  target_user_id: string | null;
  target_site_id: string | null;
  event_type: string;
  message: string | null;
  data: Record<string, unknown> | null;
}

export async function getUserAudit(userId: string, limit = 50): Promise<AuditEvent[]> {
  const response = await api.get<AuditEvent[]>(`/audit/users/${userId}?limit=${limit}`);
  return response.data;
}

export async function getSiteAudit(siteId: string, limit = 50): Promise<AuditEvent[]> {
  const response = await api.get<AuditEvent[]>(`/audit/sites/${siteId}?limit=${limit}`);
  return response.data;
}

export async function getGlobalAudit(limit = 100, eventType?: string): Promise<AuditEvent[]> {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  if (eventType) params.append('event_type', eventType);
  const response = await api.get<AuditEvent[]>(`/audit?${params.toString()}`);
  return response.data;
}

// ============== Provider Validation API ==============

export interface AmberSite {
  id: string;
  nmi: string;
  network: string;
  status: string;
  active_from?: string;
}

export interface SolcastResource {
  resource_id: string;
  name: string;
  capacity_kw: number;
  latitude: number;
  longitude: number;
  azimuth: number;
  tilt: number;
  install_date?: string;
}

/**
 * Fetch available Amber sites for an API key.
 * Used to validate the API key and populate the site selector.
 */
export async function fetchAmberSites(apiKey: string): Promise<AmberSite[]> {
  const response = await api.post<AmberSite[]>(
    `/sites/providers/amber/sites?api_key=${encodeURIComponent(apiKey)}`
  );
  return response.data;
}

/**
 * Fetch available Solcast resources for an API key.
 * Used to validate the API key and populate the resource selector.
 */
export async function fetchSolcastResources(apiKey: string): Promise<SolcastResource[]> {
  const response = await api.post<SolcastResource[]>(
    `/sites/providers/solcast/resources?api_key=${encodeURIComponent(apiKey)}`
  );
  return response.data;
}

// ============== ESY Sunhome Validation API ==============

export interface ESYDevice {
  device_sn: string;
  device_id: string;
  user_id: string;
  device_name?: string;
  model?: string;
}

/**
 * Validate ESY Sunhome credentials and fetch device information.
 * Used to verify ESY app username/password and auto-discover the device serial number.
 */
export async function validateESYSunhome(username: string, password: string): Promise<ESYDevice> {
  const response = await api.post<ESYDevice>(
    `/sites/providers/esy_sunhome/validate?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`
  );
  return response.data;
}

// ============== Telemetry API ==============

// Latest solar-curtailment decision, merged into telemetry responses by the
// API from battery_controller's solar:curtailment:{site_id} Redis key.
// Absent when the site has no solar control or no recent decision.
export interface SolarControlState {
  active: boolean;
  mode: 'applied' | 'suggested';
  action: 'limit' | 'clear';
  limit_w?: number | null;
  export_mode?: boolean;
  reason?: string;
  feed_in_price?: number | null;
  success?: boolean;
  updated_at?: string;
}

export interface LiveTelemetryResponse {
  site_id: string;
  adapter: string;
  timestamp: string;
  solar_control?: SolarControlState;
  telemetry: {
    timestamp: string;
    battery: {
      soc_percent: number;
      soh_percent: number;
      power_w: number;
      voltage_v: number;
      current_a: number;
      status: number;
      status_text: string;
      import_kwh: number;
      export_kwh: number;
      temp_c: number;
    };
    pv: {
      power_w: number;
      pv1_power_w: number;
      pv2_power_w: number;
      daily_generation_kwh: number;
    };
    grid: {
      power_w: number;
      voltage_v: number;
      frequency_hz: number;
      import_kwh: number;
      export_kwh: number;
    };
    load: {
      power_w: number;
      daily_consumption_kwh: number;
    };
    inverter: {
      temp_c: number;
      dcdc_temp_c: number;
      rated_power_w: number;
    };
    system: {
      run_mode: number;
      run_mode_text: string;
      on_grid: boolean;
      grid_connected: boolean;
      pv_connected: boolean;
      load_connected: boolean;
      battery_connected: boolean;
    };
  };
}

export async function getLiveTelemetry(siteId: string): Promise<LiveTelemetryResponse> {
  const response = await api.get<LiveTelemetryResponse>(`/telemetry/sites/${siteId}/live`);
  return response.data;
}

// Current telemetry response (from Redis - populated by battery_interface service)
// Uses same nested structure as LiveTelemetryResponse.telemetry
/**
 * Last export (feed-in) limit battery_interface applied to the inverter, as
 * a % of rated AC power. `restricted` is false when it's the uncapped
 * default the controller writes outside its managed windows.
 */
export interface ExportLimitState {
  percent: number;
  watts: number | null;
  restricted: boolean;
  applied: boolean;
  source: 'auto' | 'manual' | string;
  triggered_by?: string | null;
  timestamp: string;
}

export interface CurrentTelemetryResponse {
  site_id?: string;
  timestamp: string;
  battery: {
    soc_percent: number;
    soh_percent: number;
    power_w: number;
    voltage_v: number;
    current_a: number;
    status: number;
    status_text: string;
    import_kwh: number;
    export_kwh: number;
    temp_c: number;
  };
  pv: {
    power_w: number;
    pv1_power_w: number;
    pv2_power_w: number;
    daily_generation_kwh: number;
  };
  grid: {
    power_w: number;
    voltage_v: number;
    frequency_hz: number;
    import_kwh: number;
    export_kwh: number;
  };
  load: {
    power_w: number;
    daily_consumption_kwh: number;
  };
  inverter: {
    temp_c: number;
    dcdc_temp_c: number;
    rated_power_w: number;
  };
  system: {
    run_mode: number;
    run_mode_text: string;
    on_grid: boolean;
    grid_connected: boolean;
    pv_connected: boolean;
    load_connected?: boolean;
    battery_connected: boolean;
  };
  solar_control?: SolarControlState;
  export_limit?: ExportLimitState;
}

export async function getCurrentTelemetry(siteId: string): Promise<CurrentTelemetryResponse> {
  const response = await api.get<CurrentTelemetryResponse>(`/telemetry/sites/${siteId}/current`);
  return response.data;
}

// Connection status
export interface ConnectionStatus {
  site_id: string;
  status: 'connected' | 'connecting' | 'error' | 'disconnected' | 'unknown';
  error: string | null;
  retry_count: number;
  max_retries: number;
  updated_at: string | null;
}

export async function getConnectionStatus(siteId: string): Promise<ConnectionStatus> {
  const response = await api.get<ConnectionStatus>(`/telemetry/sites/${siteId}/connection-status`);
  return response.data;
}

// ============== Distributor Tariffs (GloBird ZeroHero) ==============

export interface DistributorTariff {
  id: string;
  retailer: string;
  distributor: string;
  state: string;
  name: string;
  code: string | null;
  structure: Record<string, unknown>;
  is_public: boolean;
}

/**
 * A geocoded place: the subset of Nominatim's fields the address pickers use.
 * Served by our API (`/geocode/*`) so the browser never contacts the
 * geocoder itself (no user IP or cookies reach it).
 */
export interface GeocodedPlace {
  display_name: string;
  lat: string;
  lon: string;
  address: {
    house_number?: string;
    road?: string;
    suburb?: string;
    town?: string;
    city?: string;
    village?: string;
    state?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
}

/** Address search (public; Australia by default). */
export async function geocodeSearch(q: string, countrycodes = 'au', limit = 5): Promise<GeocodedPlace[]> {
  const response = await api.get<GeocodedPlace[]>('/geocode/search', {
    params: { q, countrycodes, limit },
  });
  return response.data;
}

/** Reverse geocode a point (public). Null when nothing is there. */
export async function geocodeReverse(lat: number, lon: number): Promise<GeocodedPlace | null> {
  const response = await api.get<GeocodedPlace | null>('/geocode/reverse', { params: { lat, lon } });
  return response.data;
}

/**
 * List selectable distributor tariffs (public reference data — works without
 * auth so it can populate the registration selector before login).
 */
export async function getDistributorTariffs(params?: {
  state?: string;
  retailer?: string;
}): Promise<DistributorTariff[]> {
  const response = await api.get<DistributorTariff[]>('/distributor-tariffs', { params });
  return response.data;
}

/**
 * Match tariffs to a customer's postcode using the CDR plan geography. May
 * return more than one when the postcode straddles distributor boundaries.
 */
export async function matchDistributorTariffs(
  postcode: string,
  retailer = 'globird',
): Promise<DistributorTariff[]> {
  const response = await api.get<DistributorTariff[]>('/distributor-tariffs/match', {
    params: { postcode, retailer },
  });
  return response.data;
}

// ============== NEM (AEMO) API ==============

export async function getNEMPrices(): Promise<NEMPricesResponse> {
  const response = await api.get<NEMPricesResponse>('/nem/prices');
  return response.data;
}

export async function getNEMRegionPrice(region: string): Promise<NEMRegionData> {
  const response = await api.get<NEMRegionData>(`/nem/prices/${region}`);
  return response.data;
}

// Detailed NEM prices with 30-minute blocks
export interface NEMPeriod {
  period: number;
  start_time: string;
  end_time: string;
  price_kwh: number;
  price_mwh: number;
  is_current: boolean;
  is_actual: boolean;
}

export interface NEMForecastBlock {
  start_time: string;
  end_time: string;
  price_kwh: number;
  price_mwh: number;
  is_forecast?: boolean;
}

export interface NEMHistoricalBlock {
  start_time: string;
  end_time: string;
  price_kwh: number;
  price_mwh: number;
  is_settled: boolean;
  renewable_percentage?: number;
}

export interface NEMDetailedPrices {
  timestamp: string;
  region: string;
  source: string;
  current: {
    period: number;
    price_kwh: number;
    price_mwh: number;
  };
  current_30min: {
    start_time: string;
    end_time: string;
    periods_completed: number;
    average_kwh: number;
    estimate_kwh: number;
    forecast_kwh: number;
  };
  periods: NEMPeriod[];
  historical_30min_blocks?: NEMHistoricalBlock[];  // Last 6 settled blocks
  forecast_30min_blocks: NEMForecastBlock[];  // Next 6 forecast blocks
  generation: {
    total_demand_mw: number;
    scheduled_generation_mw: number;
    semi_scheduled_generation_mw: number;
    renewable_percentage: number;
  };
}

export async function getNEMDetailedPrices(region: string): Promise<NEMDetailedPrices> {
  const response = await api.get<NEMDetailedPrices>(`/nem/prices/${region}/detailed`);
  return response.data;
}

// ============== Site-specific Prices & Forecasts ==============

export interface SitePrices {
  site_id: string;
  provider: string;
  region: string;
  timestamp: string;
  // Flow Power Happy Hour sites: today's window export vs the daily cap.
  happy_hour?: {
    date: string | null;
    window_export_kwh: number;
    daily_cap_kwh: number;
    cap_reached_at: string | null;
  } | null;
  current: {
    timestamp?: string;
    import: number;  // Note: API returns 'import' not 'import_price'
    export: number;  // Note: API returns 'export' not 'export_price'
  };
  forecast?: Array<{
    timestamp: string;
    import: number;
    export: number;
  }>;
  detailed?: NEMDetailedPrices['current_30min'] & {
    periods?: NEMDetailedPrices['periods'];
    historical_30min_blocks?: NEMDetailedPrices['historical_30min_blocks'];
    forecast_30min_blocks?: NEMDetailedPrices['forecast_30min_blocks'];
    generation?: NEMDetailedPrices['generation'];
  };
}

export interface SiteForecast {
  site_id: string;
  provider: string;
  updated_at: string;
  resource_ids?: string[];
  summary: {
    today_kwh: number;
    tomorrow_kwh: number;
    peak_w: number;
    data_points: number;
  };
  forecast: Array<{
    timestamp: string;
    value_w: number;
  }>;
}

/**
 * Get prices for a specific site.
 * Returns Amber tariffs for Amber-configured sites, or AEMO regional prices otherwise.
 */
export async function getSitePrices(siteId: string): Promise<SitePrices> {
  const response = await api.get<SitePrices>(`/sites/${siteId}/prices`);
  return response.data;
}

/**
 * Get solar forecast for a specific site.
 * Returns cached Solcast forecast data.
 */
export async function getSiteForecast(siteId: string): Promise<SiteForecast> {
  const response = await api.get<SiteForecast>(`/sites/${siteId}/forecast`);
  return response.data;
}

// ============== Provider Status ==============

export interface ProviderStatusItem {
  provider: string;
  status: 'connected' | 'error' | 'not_configured' | 'pending' | 'unknown';
  error?: string | null;
  last_success?: string | null;
  updated_at?: string | null;
}

export interface SiteProviderStatus {
  site_id: string;
  price_provider?: ProviderStatusItem;
  forecast_provider?: ProviderStatusItem;
  battery_adapter?: ProviderStatusItem;
  solar_control_adapter?: ProviderStatusItem;
}

/**
 * Get provider connection status for a site.
 * Returns the status of price, forecast, and battery providers.
 */
export async function getSiteProviderStatus(siteId: string): Promise<SiteProviderStatus> {
  const response = await api.get<SiteProviderStatus>(`/sites/${siteId}/provider-status`);
  return response.data;
}

// ============== Optimization Plan ==============

export interface BatterySchedulePoint {
  timestamp: string;
  power_w: number;
  grid_power_w: number | null;  // Positive = import, Negative = export
  pv_power_w: number | null;  // Solar/PV power in Watts
  soc: number | null;
  mode: 'charge' | 'discharge' | 'idle' | null;
  import_price: number | null;
  export_price: number | null;
  // Planned EV charging power (W) from the optimizer's deferrable-load
  // schedule. Null/absent for every point when no EV is in the plan.
  ev_power_w?: number | null;
}

export interface OptimizationPlan {
  site_id: string;
  optimized_at: string | null;
  schedule: BatterySchedulePoint[];
  summary: {
    total_discharge_kwh?: number;
    total_charge_kwh?: number;
    max_discharge_w?: number;
    max_charge_w?: number;
    max_export_w?: number;
    intervals?: number;
  };
  raw_results?: Record<string, unknown> | null;
}

/**
 * Get the latest optimization plan for a site.
 * Returns the battery charge/discharge schedule from the optimizer.
 */
export async function getOptimizationPlan(siteId: string): Promise<OptimizationPlan> {
  const response = await api.get<OptimizationPlan>(`/sites/${siteId}/optimization-plan`);
  return response.data;
}

// ============== Energy Flow ==============

export interface EnergyFlowPoint {
  timestamp: string;
  import_wh: number;
  consumption_wh: number;
  production_wh: number;
  export_wh: number;
}

export interface EnergyFlowResponse {
  site_id: string;
  intervals: EnergyFlowPoint[];
  summary: {
    total_import_kwh: number;
    total_export_kwh: number;
    total_production_kwh: number;
    total_consumption_kwh: number;
    interval_count: number;
    period_hours: number;
    date?: string;            // set when fetched for a specific site-local day
    timezone?: string | null; // IANA zone used to bucket that day
  };
}

/**
 * Get energy flow data for a site.
 * Returns half-hourly energy intervals for the energy flow chart.
 */
export async function getEnergyFlow(siteId: string, hours: number = 24): Promise<EnergyFlowResponse> {
  const response = await api.get<EnergyFlowResponse>(`/sites/${siteId}/energy-flow?hours=${hours}`);
  return response.data;
}

/**
 * Get energy flow data for one site-local calendar day (YYYY-MM-DD).
 * Used by the Energy History page to drill into a daily-totals row.
 */
export async function getEnergyFlowForDay(
  siteId: string,
  date: string
): Promise<EnergyFlowResponse> {
  const response = await api.get<EnergyFlowResponse>(
    `/sites/${siteId}/energy-flow?date=${encodeURIComponent(date)}`
  );
  return response.data;
}

// ============== Energy Cost Totals ==============

export interface CostTotalsResponse {
  site_id: string;
  period: 'today' | 'week' | 'month';
  start_date: string;
  end_date: string;
  total_import_kwh: number;
  total_export_kwh: number;
  total_import_cost: number;  // Approximate cost in $
  total_export_value: number;  // Approximate value in $
  net_cost: number;  // import_cost - export_value
  intervals_count: number;
  billed_intervals_count?: number;  // intervals carrying Amber's billed $ (rest are estimates)
}

/**
 * Get energy cost totals for a site.
 * Returns aggregated costs for today, week, or month.
 */
export async function getCostTotals(
  siteId: string,
  period: 'today' | 'week' | 'month' = 'today'
): Promise<CostTotalsResponse> {
  const response = await api.get<CostTotalsResponse>(`/sites/${siteId}/costs?period=${period}`);
  return response.data;
}

// ============== Public Contact Form ==============

export interface ContactRequest {
  name: string;
  email: string;
  subject: string;
  message: string;
  /** Honeypot — must remain empty for the request to be processed. */
  website?: string;
}

/**
 * POST a contact-form submission. Public endpoint, rate-limited per-IP.
 * Returns `{ ok: true }` on accept (or honeypot trip).
 */
export async function submitContactForm(payload: ContactRequest): Promise<{ ok: boolean }> {
  const response = await api.post<{ ok: boolean }>('/contact', {
    name: payload.name,
    email: payload.email,
    subject: payload.subject,
    message: payload.message,
    website: payload.website ?? '',
  });
  return response.data;
}

// ============== Diagnostics Export ==============

/**
 * Bundle of cached optimizer + battery state for offline analysis.
 * Returned by GET /sites/{id}/diagnostics-export. Admin only.
 */
export type DiagnosticsExport = Record<string, unknown>;

export async function exportSiteDiagnostics(
  siteId: string,
  options: { intervalsHours?: number; historyDays?: number; modeEventsLimit?: number } = {}
): Promise<DiagnosticsExport> {
  const params = new URLSearchParams();
  if (options.intervalsHours) params.append('intervals_hours', String(options.intervalsHours));
  if (options.historyDays) params.append('history_days', String(options.historyDays));
  if (options.modeEventsLimit) params.append('mode_events_limit', String(options.modeEventsLimit));
  const qs = params.toString();
  const response = await api.get<DiagnosticsExport>(
    `/sites/${siteId}/diagnostics-export${qs ? `?${qs}` : ''}`
  );
  return response.data;
}

// ============== Daily Energy History ==============

export interface DailyEnergyTotal {
  date: string;  // YYYY-MM-DD in the site's local timezone
  total_import_kwh: number;
  total_export_kwh: number;
  total_production_kwh: number;
  total_consumption_kwh: number;
  total_import_cost: number;
  total_export_value: number;
  net_cost: number;
  intervals_count: number;
  is_partial?: boolean;  // true for the still-open "today" row
  source?: string;       // 'counters' | 'intervals' | 'live'
  // 'amber' = the $ figures are Amber's billed usage for the day;
  // 'estimated' = SolanIQ priced the inverter's readings itself.
  price_source?: 'amber' | 'estimated' | string;
  billed_import_kwh?: number | null;   // meter kWh per Amber
  billed_export_kwh?: number | null;
  estimated_import_cost?: number | null;  // our figure, when billed replaced it
  estimated_export_value?: number | null;
  billed_at?: string | null;
}

export interface EnergyHistoryResponse {
  site_id: string;
  days_requested: number;
  start_date: string;
  end_date: string;
  timezone?: string | null;
  days: DailyEnergyTotal[];
  summary: {
    total_import_kwh: number;
    total_export_kwh: number;
    total_production_kwh: number;
    total_consumption_kwh: number;
    total_import_cost: number;
    total_export_value: number;
    net_cost: number;
    days_with_data: number;
    billed_days?: number;
  };
}

/**
 * Get daily energy history for a site.
 * Aggregates 30-min intervals into per-day totals for the last `days` days.
 */
export async function getEnergyHistory(
  siteId: string,
  days: number = 30
): Promise<EnergyHistoryResponse> {
  const response = await api.get<EnergyHistoryResponse>(
    `/sites/${siteId}/energy-history?days=${days}`
  );
  return response.data;
}

export type CsvExportKind = 'readings' | 'daily';

/**
 * Download a site's recorded data as CSV for a range of site-local days
 * (YYYY-MM-DD, inclusive). `readings` = one row per ~5-minute snapshot
 * (kWh for the interval + instantaneous W); `daily` = per-day kWh totals.
 * Saves the file under the name the API suggests.
 */
export async function downloadSiteCsv(
  siteId: string,
  start: string,
  end: string,
  kind: CsvExportKind
): Promise<void> {
  let response;
  try {
    response = await api.get<Blob>(`/sites/${siteId}/export.csv`, {
      params: { start, end, kind },
      responseType: 'blob',
    });
  } catch (err) {
    // With responseType 'blob' the error body is a Blob too; surface its detail.
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (data instanceof Blob) {
      let detail: unknown;
      try {
        detail = JSON.parse(await data.text())?.detail;
      } catch {
        detail = undefined;
      }
      if (typeof detail === 'string') throw new Error(detail);
    }
    throw err;
  }
  const disposition = String(response.headers['content-disposition'] ?? '');
  const filename =
    /filename="([^"]+)"/.exec(disposition)?.[1] ?? `solaniq-${kind}-${start}_${end}.csv`;
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ============== Battery Mode Events ==============

export interface BatteryModeEvent {
  id: string;
  timestamp: string;
  // "site.battery_mode" | "site.export_limit" | "site.control_conflict"
  event_type?: string;
  actor_email: string | null;
  inverter_mode: string | null;
  inverter_mode_code: number | null;
  mode: string | null;
  source: string | null;  // "manual" | "auto" | "suggestion" | "conflict"
  export_limit_percent?: number | null;
  export_limit_w?: number | null;
  message: string | null;
  data: Record<string, unknown> | null;
}

/**
 * Get recent battery mode change events for a site.
 */
export async function getBatteryModeEvents(
  siteId: string,
  limit: number = 100
): Promise<BatteryModeEvent[]> {
  const response = await api.get<BatteryModeEvent[]>(
    `/sites/${siteId}/battery-mode-events?limit=${limit}`
  );
  return response.data;
}

// ============== Activity Feed ==============

export interface ActivityEvent {
  id: string;
  timestamp: string;
  event_type: 'optimization' | 'price_update' | 'telemetry' | 'alert' | 'status';
  site_id: string | null;
  site_name: string | null;
  message: string;
  details: Record<string, unknown> | null;
}

export interface ActivityFeed {
  events: ActivityEvent[];
  total: number;
}

/**
 * Get recent activity events.
 * Returns optimization runs, price updates, telemetry changes, etc.
 */
export async function getActivityFeed(limit: number = 20, siteId?: string): Promise<ActivityFeed> {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  if (siteId) params.append('site_id', siteId);

  const response = await api.get<ActivityFeed>(`/sites/activity/feed?${params.toString()}`);
  return response.data;
}

// ============== Performance Metrics ==============

export interface OperationMetrics {
  operation: string;
  count: number;
  success_count: number;
  failure_count: number;
  success_rate: number;
  avg_ms: number;
  min_ms: number;
  max_ms: number;
  p95_ms: number;
  last_success: string | null;
  last_failure: string | null;
  last_error: string | null;
}

export interface AdapterMetrics {
  adapter_name: string;
  operations: OperationMetrics[];
}

export interface SiteMetrics {
  site_id: string;
  generated_at: string;
  adapters: {
    price_provider?: AdapterMetrics;
    battery_interface?: AdapterMetrics;
    optimizer?: AdapterMetrics;
    forecast_provider?: AdapterMetrics;
  };
}

export interface AllSitesMetricsSummary {
  total_sites: number;
  sites_with_metrics: number;
  adapter_summaries: {
    [key: string]: {
      total_operations: number;
      total_successes: number;
      total_failures: number;
      avg_latency_ms: number;
      max_latency_ms: number;
      sites_reporting: number;
    };
  };
}

/**
 * Get performance metrics for a site.
 * Returns adapter latencies, success rates, and operation counts.
 */
export async function getSiteMetrics(siteId: string): Promise<SiteMetrics> {
  const response = await api.get<SiteMetrics>(`/sites/${siteId}/metrics`);
  return response.data;
}

/**
 * Get aggregated performance metrics across all sites.
 */
export async function getAllSitesMetricsSummary(): Promise<AllSitesMetricsSummary> {
  const response = await api.get<AllSitesMetricsSummary>('/sites/metrics/summary');
  return response.data;
}

// ============== Optimization Trigger ==============

export interface OptimizationTriggerResponse {
  status: string;
  site_id: string;
  optimization_type: string;
  message: string;
}

/**
 * Trigger optimization for a site (admin only).
 * @param siteId - Site ID
 * @param optimizationType - "dayahead" or "mpc"
 */
export async function triggerOptimization(
  siteId: string,
  optimizationType: 'dayahead' | 'mpc' = 'mpc'
): Promise<OptimizationTriggerResponse> {
  const response = await api.post<OptimizationTriggerResponse>(
    `/sites/${siteId}/optimization/trigger`,
    { optimization_type: optimizationType }
  );
  return response.data;
}

// ============== Forecast Refresh ==============

export interface ForecastRefreshResponse {
  status: string;
  site_id: string;
  provider?: string;
  scenario?: string;
  data_points: number;
  summary: {
    today_kwh: number;
    tomorrow_kwh: number;
    peak_w: number;
  };
  message: string;
}

/**
 * Manually refresh the solar forecast for a site.
 */
export async function refreshForecast(siteId: string): Promise<ForecastRefreshResponse> {
  const response = await api.post<ForecastRefreshResponse>(`/sites/${siteId}/forecast/refresh`);
  return response.data;
}

// ============== Price Refresh ==============

export interface PriceRefreshResponse {
  status: string;
  site_id: string;
  provider: string;
  scenario?: string;
  current: {
    import_price: number;
    export_price: number;
    timestamp: string;
  };
  forecast_count: number;
  message: string;
}

/**
 * Manually refresh the simulated prices for a site (admin only).
 */
export async function refreshPrices(siteId: string): Promise<PriceRefreshResponse> {
  const response = await api.post<PriceRefreshResponse>(`/sites/${siteId}/prices/refresh`);
  return response.data;
}

// ============== Battery Mode Control ==============

export interface BatteryModeChangeResponse {
  status: string;
  site_id: string;
  inverter_mode: string | null;
  message: string;
  /** Export limit (% of rated power) sent with the command, when any. */
  export_limit_percent?: number | null;
  /** Whether battery_interface reported the export limit as applied. */
  export_limit_applied?: boolean | null;
}

/**
 * Manually set the battery inverter mode for a site (admin only).
 *
 * @param siteId - Site ID
 * @param inverterModeCode - ESY mode code: 1=Regular, 3=Discharge, 4=Charge, 5=BEM
 */
export async function setBatteryMode(
  siteId: string,
  inverterModeCode: number
): Promise<BatteryModeChangeResponse> {
  const response = await api.post<BatteryModeChangeResponse>(
    `/sites/${siteId}/battery/mode`,
    { inverter_mode_code: inverterModeCode }
  );
  return response.data;
}

// ============== EV Chargers (OCPP) ==============

export interface EVChargingPrefs {
  target_kwh?: number | null;   // energy to deliver per plug-in session
  // State-of-charge target (needs the battery size; wins over target_kwh)
  target_soc_percent?: number | null;
  vehicle_battery_kwh?: number | null;
  plug_in_soc_percent?: number | null; // typical SOC on arrival
  ready_by?: string | null;     // local time "HH:MM" charging must finish by
  smart_enabled: boolean;       // false = charge immediately at full rate
  solar_only: boolean;          // follow the site's solar surplus instead of the price plan
  charger_manages_solar?: boolean; // with solar_only: the charger's own solar mode, SolanIQ hands off
  grid_top_up?: boolean; // with solar_only: plan tops up from the grid by ready_by
  manual_amps?: number | null; // manual charge speed (A), held instead of plan/solar
}

/** "ocpp" = smart charger the gateway controls; "manual" = declared sessions only. */
export type EVChargerControlType = 'ocpp' | 'manual';

/** Live charger status published by the OCPP gateway (Redis). */
export interface EVChargerStatus {
  charger_id: string;
  charge_point_id: string;
  name: string;
  site_id: string;
  control_type?: EVChargerControlType;
  /** false for manual chargers: the plan models them as a fixed load. */
  controllable?: boolean;
  connected: boolean;
  connector_status: string;
  plugged_in: boolean;
  charging: boolean;
  plugged_in_since: string | null;
  transaction_id: number | null;
  session_energy_wh: number;
  /** Delivered since plug-in, across transactions (OCPP chargers). */
  episode_energy_wh?: number;
  power_w: number | null;
  /** "measured" | "current" (I x V) | "derived" (energy register slope) */
  power_source?: string | null;
  current_limit_a: number | null;
  boost: boolean;
  deadline_boost?: boolean;
  /** Target bookkeeping (OCPP chargers with a kWh or % target). */
  target_label?: string | null;
  target_wh?: number | null;
  remaining_wh?: number | null;
  target_reached?: boolean;
  /** SOC reported by the charger itself (rare on AC) */
  soc_percent?: number | null;
  plug_in_soc_percent?: number | null;
  /** Per-plug-in override entered by the owner (null = using the pref) */
  plug_in_soc_session?: number | null;
  estimated_soc_percent?: number | null;
  suspended_by_plan: boolean;
  next_window_start: string | null;
  smart_enabled: boolean;
  solar_only?: boolean;
  charger_manages_solar?: boolean;
  grid_top_up?: boolean;
  /** true while a plan top-up slot (grid) drives the charger */
  top_up_active?: boolean;
  /** Manual charge speed in force (A, clamped to the charger), or null. */
  manual_amps?: number | null;
  prefs: EVChargingPrefs | null;
  /** Manual chargers only: the declared session, when one is active. */
  session_id?: string | null;
  expected_end_at?: string | null;
  declared_power_w?: number | null;
  baseline_load_w?: number | null;
  max_power_w: number;
  min_power_w: number;
  max_current_a: number;
  /** Phases the plugged-in car actually draws on (learned from the meter). */
  vehicle_phases?: number | null;
  effective_phases?: number;
  effective_max_power_w?: number;
  timezone: string | null;
  updated_at: string;
}

/** Values the user enters into the physical charger's OCPP settings. */
export interface EVChargerConnection {
  ws_url: string;
  charge_point_id: string;
  auth_password: string | null;
}

export interface EVCharger {
  id: string;
  site_id: string;
  charge_point_id: string;
  name: string;
  control_type: EVChargerControlType;
  vendor: string | null;
  model: string | null;
  firmware_version: string | null;
  max_current_a: number;
  min_current_a: number;
  phases: number;
  voltage_v: number;
  max_power_w: number;
  charging_prefs: EVChargingPrefs | null;
  config: Record<string, unknown> | null;
  enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
  /** Live status, or null when the charger isn't reporting. */
  status?: EVChargerStatus | null;
  /** Present on create and single-charger GET responses (OCPP chargers). */
  connection?: EVChargerConnection;
  /** Manual chargers: the active declared session, or null. */
  active_session?: EVChargingSession | null;
}

export interface EVChargerCreate {
  name: string;
  control_type?: EVChargerControlType;
  max_current_a?: number;
  min_current_a?: number;
  phases?: number;
  voltage_v?: number;
  charging_prefs?: EVChargingPrefs;
}

export interface EVChargerUpdate {
  name?: string;
  max_current_a?: number;
  min_current_a?: number;
  phases?: number;
  voltage_v?: number;
  charging_prefs?: EVChargingPrefs;
  enabled?: boolean;
}

/** One charging session: an OCPP transaction, or a user-declared window on a manual charger. */
export interface EVChargingSession {
  id: string;
  charger_id: string;
  site_id: string;
  source: 'ocpp' | 'manual';
  ocpp_transaction_id: number | null;
  connector_id: number;
  id_tag: string | null;
  started_at: string;
  ended_at: string | null;
  meter_start_wh: number | null;
  meter_stop_wh: number | null;
  energy_wh: number;
  stop_reason: string | null;
  status: string;  // 'active' | 'completed'
  /** Manual sessions only. */
  expected_end_at?: string | null;
  declared_power_w?: number | null;
  baseline_load_w?: number | null;
}

/** Declare / adjust a charging session on a manual charger. */
export interface EVManualSessionInput {
  /** ISO 8601 end time; alternative to duration_minutes. */
  until?: string;
  duration_minutes?: number;
  /** Charging power in W; defaults to the charger's maximum. */
  power_w?: number;
}

export type EVChargerCommandName =
  | 'boost_on'
  | 'boost_off'
  | 'stop'
  | 'set_limit'
  | 'set_plug_in_soc';

export interface EVChargerCommandResponse {
  success: boolean;
  message: string;
  request_id?: string;
}

/**
 * Live EV status for the site's charger, or null when the site has no
 * charger reporting (the API answers 404). Used by the power flow card.
 */
export async function getEVStatus(siteId: string): Promise<EVChargerStatus | null> {
  try {
    const response = await api.get<EVChargerStatus>(`/sites/${siteId}/ev/status`);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
}

/** List a site's EV chargers, each merged with its live gateway status. */
export async function getEVChargers(siteId: string): Promise<EVCharger[]> {
  const response = await api.get<EVCharger[]>(`/sites/${siteId}/ev-chargers`);
  return response.data;
}

/**
 * Provision an EV charger for a site. The response's `connection`
 * (ws_url / charge_point_id / auth_password) must be shown to the user —
 * they enter it into the charger's OCPP settings.
 */
export async function createEVCharger(
  siteId: string,
  data: EVChargerCreate
): Promise<EVCharger> {
  const response = await api.post<EVCharger>(`/sites/${siteId}/ev-chargers`, data);
  return response.data;
}

/** Get one charger with its connection info and live status. */
export async function getEVCharger(siteId: string, chargerId: string): Promise<EVCharger> {
  const response = await api.get<EVCharger>(`/sites/${siteId}/ev-chargers/${chargerId}`);
  return response.data;
}

export async function updateEVCharger(
  siteId: string,
  chargerId: string,
  data: EVChargerUpdate
): Promise<EVCharger> {
  const response = await api.patch<EVCharger>(
    `/sites/${siteId}/ev-chargers/${chargerId}`,
    data
  );
  return response.data;
}

export async function deleteEVCharger(siteId: string, chargerId: string): Promise<void> {
  await api.delete(`/sites/${siteId}/ev-chargers/${chargerId}`);
}

/** Charging session history for a charger, newest first. */
export async function getEVChargerSessions(
  siteId: string,
  chargerId: string,
  days: number = 30
): Promise<EVChargingSession[]> {
  const response = await api.get<EVChargingSession[]>(
    `/sites/${siteId}/ev-chargers/${chargerId}/sessions?days=${days}`
  );
  return response.data;
}

/**
 * Send a manual command to the charger via the gateway. Waits for the
 * gateway's acknowledgement, so this can take up to ~8 seconds; a timeout
 * comes back as `{ success: false }` rather than an HTTP error.
 */
export async function sendEVChargerCommand(
  siteId: string,
  chargerId: string,
  command: EVChargerCommandName,
  amps?: number,
  percent?: number | null
): Promise<EVChargerCommandResponse> {
  const payload: { command: EVChargerCommandName; amps?: number; percent?: number | null } = {
    command,
  };
  if (amps !== undefined) payload.amps = amps;
  if (command === 'set_plug_in_soc') payload.percent = percent ?? null;
  const response = await api.post<EVChargerCommandResponse>(
    `/sites/${siteId}/ev-chargers/${chargerId}/commands`,
    payload
  );
  return response.data;
}

/** Start a declared charging session on a manual (non-OCPP) charger. */
export async function startEVManualSession(
  siteId: string,
  chargerId: string,
  input: EVManualSessionInput
): Promise<EVChargingSession> {
  const response = await api.post<EVChargingSession>(
    `/sites/${siteId}/ev-chargers/${chargerId}/sessions`,
    input
  );
  return response.data;
}

/** Extend / re-time / re-rate the active declared session. */
export async function updateEVManualSession(
  siteId: string,
  chargerId: string,
  sessionId: string,
  input: EVManualSessionInput
): Promise<EVChargingSession> {
  const response = await api.patch<EVChargingSession>(
    `/sites/${siteId}/ev-chargers/${chargerId}/sessions/${sessionId}`,
    input
  );
  return response.data;
}

/** End the active declared session now. */
export async function endEVManualSession(
  siteId: string,
  chargerId: string,
  sessionId: string
): Promise<EVChargingSession> {
  const response = await api.post<EVChargingSession>(
    `/sites/${siteId}/ev-chargers/${chargerId}/sessions/${sessionId}/end`
  );
  return response.data;
}

/**
 * Admin only: re-send Sell mode together with the current export limit,
 * exactly as the battery controller does on a plan event. Some ESY units
 * revert their export cap on pattern entry / reconnect and then sit in Sell
 * mode exporting nothing; a plain mode change sends no limit, this does.
 *
 * @param siteId - Site ID
 * @param exportLimitPercent - optional override; defaults to the last limit applied
 */
export async function resendSellWithExportLimit(
  siteId: string,
  exportLimitPercent?: number
): Promise<BatteryModeChangeResponse> {
  const response = await api.post<BatteryModeChangeResponse>(
    `/sites/${siteId}/battery/resend-sell`,
    exportLimitPercent != null ? { export_limit_percent: exportLimitPercent } : {}
  );
  return response.data;
}

// Error handler
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    throw error;
  }
);

export default api;
